import { useState, useEffect } from "react";
import { toast } from "sonner";
import { getProyeccionDefault, saveProyeccionDefault } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { SlidersHorizontal, Loader2 } from "lucide-react";

const OPCIONES = [
  {
    value: "defecto",
    label: "Por defecto",
    descripcion: "Comportamiento actual (plano, o IPC en gastos si hay inflación configurada en Drivers → Generales).",
  },
  {
    value: "plano",
    label: "Todo plano",
    descripcion: "Todas las líneas sin driver se mantienen planas al último año real.",
  },
  {
    value: "ipc",
    label: "Todo IPC",
    descripcion: "Todas las líneas sin driver crecen a un IPC anual.",
  },
];

export default function ProyeccionDefaultButton({ onSaved }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modo, setModo] = useState("defecto");
  const [ipcPct, setIpcPct] = useState("");

  useEffect(() => {
    if (!open) return;
    let cancel = false;
    (async () => {
      try {
        setLoading(true);
        const { data } = await getProyeccionDefault();
        if (cancel) return;
        setModo(data?.modo || "defecto");
        setIpcPct(data?.ipc_pct != null ? String(data.ipc_pct) : "");
      } catch (e) {
        if (!cancel) toast.error(e?.response?.data?.detail || "Error al cargar la preferencia");
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [open]);

  const handleSave = async () => {
    const payload = { modo };
    if (modo === "ipc") {
      const parsed = parseFloat(String(ipcPct).replace(",", "."));
      if (isNaN(parsed)) {
        toast.error("Introduce un IPC anual válido (%)");
        return;
      }
      payload.ipc_pct = parsed;
    }
    try {
      setSaving(true);
      await saveProyeccionDefault(payload);
      toast.success("Preferencia de proyección guardada");
      setOpen(false);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar la preferencia");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-auto gap-2"
          data-testid="proyeccion-default-trigger"
        >
          <SlidersHorizontal className="h-4 w-4" />
          <span className="hidden sm:inline">Defecto de proyección</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80" align="end" data-testid="proyeccion-default-popover">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-1">
              <h4 className="font-medium text-sm">Defecto de proyección</h4>
              <p className="text-xs text-muted-foreground">
                Se aplica a <strong>todas tus empresas</strong> y solo a las líneas
                <strong> sin driver propio</strong>. El driver de cada línea siempre manda por encima de esto.
              </p>
            </div>

            <RadioGroup
              value={modo}
              onValueChange={setModo}
              className="space-y-3"
              data-testid="proyeccion-default-radiogroup"
            >
              {OPCIONES.map((op) => (
                <div key={op.value} className="flex items-start gap-2">
                  <RadioGroupItem
                    value={op.value}
                    id={`proy-def-${op.value}`}
                    className="mt-0.5"
                    data-testid={`proyeccion-default-opt-${op.value}`}
                  />
                  <div className="grid gap-0.5">
                    <Label htmlFor={`proy-def-${op.value}`} className="cursor-pointer font-normal">
                      {op.label}
                    </Label>
                    <p className="text-xs text-muted-foreground">{op.descripcion}</p>
                  </div>
                </div>
              ))}
            </RadioGroup>

            {modo === "ipc" && (
              <div className="space-y-1.5">
                <Label htmlFor="proy-def-ipc" className="text-xs">
                  IPC anual (%)
                </Label>
                <Input
                  id="proy-def-ipc"
                  type="number"
                  step="0.1"
                  inputMode="decimal"
                  placeholder="Ej. 2.5"
                  value={ipcPct}
                  onChange={(e) => setIpcPct(e.target.value)}
                  data-testid="proyeccion-default-ipc"
                />
              </div>
            )}

            <div className="flex justify-end gap-2 pt-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setOpen(false)}
                disabled={saving}
              >
                Cancelar
              </Button>
              <Button
                size="sm"
                onClick={handleSave}
                disabled={saving}
                data-testid="proyeccion-default-guardar"
              >
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                Guardar
              </Button>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
